import { collection, doc, addDoc, getDoc, updateDoc, serverTimestamp, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { PetProfile } from '../types/onboarding';
import { PetShareService } from './petShareService';
import { calculateBirthdayFromAge } from '../utils/birthdayCalculation';

export class IntakeService {
    private static readonly INTAKE_COLLECTION = 'intakeForms';

    /**
     * Generates a link to the intake form for a professional.
     */
    static generateIntakeLink(professionalId: string): string {
        const baseUrl = window.location.origin;
        return `${baseUrl}/intake/${professionalId}`;
    }

    /**
     * Submits a new intake form to be reviewed by the professional.
     */
    static async submitIntakeForm(professionalId: string, data: Partial<PetProfile>): Promise<string> {
        try {
            const intakeData = {
                ...data,
                professionalId,
                isIntakePending: true,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
                isActive: true,
            };

            const formsRef = collection(db, 'users', professionalId, this.INTAKE_COLLECTION);
            const docRef = await addDoc(formsRef, intakeData);
            return docRef.id;
        } catch (error) {
            console.error('Error submitting intake form:', error);
            throw new Error('Failed to submit intake form');
        }
    }

    /**
     * Gets all pending intake forms for a professional.
     */
    static async getPendingIntakeForms(professionalId: string): Promise<PetProfile[]> {
        try {
            const formsRef = collection(db, 'users', professionalId, this.INTAKE_COLLECTION);
            const q = query(formsRef, where('isIntakePending', '==', true));
            const querySnapshot = await getDocs(q);

            return querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })) as PetProfile[];
        } catch (error) {
            console.error('Error getting pending intake forms:', error);
            throw new Error('Failed to get pending intake forms');
        }
    }

    /**
     * Approves a pending intake form, creates a standard PetProfile, and shares it.
     */
    static async approveIntakeForm(professionalId: string, formId: string): Promise<{ petId: string, shareLink: string, petData: Partial<PetProfile> }> {
        try {
            // 1. Get the pending form
            const formRef = doc(db, 'users', professionalId, this.INTAKE_COLLECTION, formId);
            const formSnap = await getDoc(formRef);

            if (!formSnap.exists()) {
                throw new Error('Intake form not found');
            }

            const formData = formSnap.data() as PetProfile;

            const petProfilesRef = collection(db, 'users', professionalId, 'petProfiles');

            // Remove intake specific metadata before saving as normal pet profile
            const { isIntakePending, ...petDataToSave } = formData;

            // Calculate birthday from age if not present but age is present
            let petBirthday = petDataToSave.birthday;
            if (!petBirthday && petDataToSave.age) {
                try {
                    petBirthday = calculateBirthdayFromAge(petDataToSave.age);
                } catch (error) {
                    console.warn('Failed to calculate birthday from age:', error);
                }
            }

            const petDocData = {
                ...petDataToSave,
                userId: professionalId,
                breed: petDataToSave.breed || 'Unknown',
                age: petDataToSave.age || 0,
                gender: petDataToSave.gender || 'unknown',
                isActive: true,
                onboardingCompleted: true,
                onboardingStep: 'COMPLETED',
                createdAt: petDataToSave.createdAt || serverTimestamp(),
                updatedAt: serverTimestamp(),
                birthday: petBirthday || serverTimestamp(),
                profilePhoto: petDataToSave.profilePhoto || '',
            };

            const petDocRef = await addDoc(petProfilesRef, petDocData);

            // 3. Mark the intake form as no longer pending
            await updateDoc(formRef, {
                isIntakePending: false,
                approvedPetId: petDocRef.id,
                updatedAt: serverTimestamp()
            });

            // 4. Generate Share Link
            const shareLink = await PetShareService.generateShareLink(petDocRef.id, professionalId);

            return {
                petId: petDocRef.id,
                shareLink,
                petData: petDocData as unknown as Partial<PetProfile>
            };
        } catch (error) {
            console.error('Error approving intake form:', error);
            throw new Error('Failed to approve intake form');
        }
    }

    /**
     * Deletes a pending intake form.
     */
    static async deleteIntakeForm(professionalId: string, formId: string): Promise<void> {
        try {
            const formRef = doc(db, 'users', professionalId, this.INTAKE_COLLECTION, formId);
            // We can just hard-delete the document or mark it inactive. Hard delete is simpler for cleanup.
            const { deleteDoc } = await import('firebase/firestore');
            await deleteDoc(formRef);
        } catch (error) {
            console.error('Error deleting intake form:', error);
            throw new Error('Failed to delete intake form');
        }
    }
}
